import React, { useState } from 'react';
import { Grid, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@material-ui/core';
import { OnwardCard } from './Card';
import { CardTitle, CardItem } from './blocks';
import { css } from '@emotion/react';
import { PrimaryButton, SecondaryButton } from '@/styles/blocks';
import useAction from '@/utilities/useQuery';
import { post } from '@/utilities/onwardClient';
import { TRIGGER_ORDER_EVENT, UNASSIGN_CARRIER } from '@/constants/apiRoutes';
import { unassignCarrierMessage } from '@/utilities/unassignCarrierMessage';
import * as Sentry from '@sentry/react';
import { useClientUser } from '@/hooks';
import { useMutation } from '@apollo/client';
import { UPDATE_ORDER_BY_ID } from '@/graphql/mutations/orders';

// Integrations subscribed to order_sync, which pulls the order's current state back from the partner.
// The event goes nowhere for any other integration, so the button would do nothing there.
const SYNC_SUPPORTED_INTEGRATIONS = ['VAULT'];

const IntegrationPartnerCard = ({ order, integrationName, setNotification, refetchOrder }) => {
    const { isOnwardAdmin, isImposter } = useClientUser();
    const [unassignConfirmOpen, setUnassignConfirmOpen] = useState(false);
    // A split parent is frozen: hide the buttons that unassign it or send it to the partner.
    const isSplit = order?.order_status === 'split';

    const [emit, { loading: emitLoading }] = useAction(({ order_id, event }) => {
        return post(TRIGGER_ORDER_EVENT, { order_ids: [order_id], event });
    });

    // order_sync is queued, not run inline, so this only confirms the request went out. The changes
    // land a few moments later; a refresh shows them.
    const [sync, { loading: syncLoading }] = useAction(async ({ order_id }) => {
        try {
            await post(TRIGGER_ORDER_EVENT, { order_ids: [order_id], event: 'order_sync' });
            setNotification({
                severity: 'success',
                message: `Sync requested from ${integrationName}. Updates should appear on this order shortly.`,
            });
        } catch (e) {
            setNotification({
                severity: 'error',
                message: `Failed to sync from ${integrationName}. Please try again.`,
            });
            Sentry.captureException(e);
        }
    });

    const [unassign, { loading: unassignLoading }] = useAction(async ({ order_id }) => {
        try {
            // This card renders the dispatch leg throughout -- `dispatch_integration_meta` above,
            // `order_carrier.dispatch_integration_type` on the emit button -- so that is the leg it
            // unassigns. The middle mile leg is left running; it is unassigned from the admin tab.
            const resp = await post(UNASSIGN_CARRIER, { order_id, leg: 'DISPATCH' });
            setNotification(unassignCarrierMessage(resp));
            if (refetchOrder) refetchOrder();
        } catch (e) {
            setNotification({ severity: 'error', message: 'Failed to unassign carrier. Please try again.' });
            console.error(e);
        } finally {
            setUnassignConfirmOpen(false);
        }
    });

    return (
        <OnwardCard
            css={css`
                height: 100%;
            `}
            contentProps={{ style: { height: '100%' } }}
        >
            <Grid container style={{ height: '100%', flexWrap: 'nowrap' }} direction="column">
                <CardTitle>Partnered TMS Information</CardTitle>
                {integrationName && order.dispatch_integration_meta ? (
                    <>
                        <CardItem className="my-1">
                            Information for this order from the {integrationName} platform can be found below. Updates
                            from {integrationName} may take 1-2 hours to appear on Onward.
                        </CardItem>
                        {order.dispatch_integration_meta.infoFromDispatchPartner ? (
                            <>
                                <CardItem className="my-1">Information from {integrationName}: </CardItem>
                                {Object.entries(order.dispatch_integration_meta.infoFromDispatchPartner).map(
                                    (entry, i) => (
                                        <CardItem key={i} className="my-1">
                                            {entry[0]}: {entry[1]}
                                        </CardItem>
                                    )
                                )}
                            </>
                        ) : null}
                        {!isSplit && SYNC_SUPPORTED_INTEGRATIONS.includes(order.dispatch_integration_type) && (
                            <PrimaryButton
                                variant="contained"
                                style={{ width: 'fit-content', marginTop: '1rem' }}
                                disabled={syncLoading}
                                onClick={() => sync({ order_id: order.order_id })}
                            >
                                {syncLoading ? 'Syncing...' : 'Sync Order'}
                            </PrimaryButton>
                        )}
                        {isOnwardAdmin && !isSplit && (
                            <PrimaryButton
                                variant="outlined"
                                style={{ width: 'fit-content', marginTop: '1rem' }}
                                disabled={unassignLoading}
                                onClick={() => setUnassignConfirmOpen(true)}
                            >
                                Unassign Final Mile Carrier
                            </PrimaryButton>
                        )}
                    </>
                ) : isSplit ? null : (
                    <>
                        <CardItem className="my-1">
                            Information for this order has not yet been sent to your partnered integration platform (
                            {integrationName}). Click the button below if you would like to manually submit it.
                        </CardItem>
                        <PrimaryButton
                            variant="contained"
                            style={{ width: 'fit-content' }}
                            disabled={emitLoading || !order?.order_carrier?.dispatch_integration_type}
                            onClick={() => {
                                let event = 'carrier_assigned';

                                emit({
                                    order_id: order.order_id,
                                    event,
                                });
                            }}
                        >
                            Send to {integrationName}
                        </PrimaryButton>
                    </>
                )}
            </Grid>

            <Dialog open={unassignConfirmOpen} onClose={() => setUnassignConfirmOpen(false)}>
                <DialogTitle>Unassign Final Mile Carrier?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will attempt to cancel the order with the final mile integration partner, then clear the
                        final mile carrier and integration data. The middle mile carrier is left as it is. This cannot
                        be undone. Proceed?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <SecondaryButton onClick={() => setUnassignConfirmOpen(false)} disabled={unassignLoading}>
                        Cancel
                    </SecondaryButton>
                    <PrimaryButton onClick={() => unassign({ order_id: order.order_id })} disabled={unassignLoading}>
                        {unassignLoading ? 'Unassigning...' : 'Yes, Unassign'}
                    </PrimaryButton>
                </DialogActions>
            </Dialog>
        </OnwardCard>
    );
};

export default IntegrationPartnerCard;
